import { CustomSlider } from "@/components/ui/custom-slider";

interface BedroomsSectionProps {
	value?: { min: number; max: number };
	onChange: (value: { min: number; max: number }) => void;
}

export function BedroomsSection({ value = { min: 0, max: 0 }, onChange }: BedroomsSectionProps) {
	const minRange = 1;
	const maxRange = 6;

	const handleValueChange = (newValues: [number, number]) => {
		// Prevent infinite loop: Don't call onChange if this is just the initial coercion
		// from {min: 0, max: 0} to the slider's valid range [1, 6]
		if (value.min === 0 && value.max === 0 && newValues[0] === minRange && newValues[1] === maxRange) {
			// This is the initial coercion, not a user interaction - ignore it
			return;
		}

		onChange({ min: newValues[0], max: newValues[1] });
	};

	// Handle the "not set" case (0) by coercing it to the slider's bounds.
	const sliderValue: [number, number] = [
		value.min === 0 ? minRange : value.min,
		value.max === 0 ? maxRange : value.max,
	];

	// Generate tick marks
	const ticks = Array.from({ length: (maxRange - minRange) / 0.5 + 1 }, (_, i) => minRange + i * 0.5);

	return (
		<div className="space-y-6">
			{/* Custom Range Slider */}
			<div className="space-y-4">
				<div className="relative h-6 flex items-center px-3">
					<CustomSlider
						value={sliderValue}
						onChange={newValues => handleValueChange(newValues as [number, number])}
						min={minRange}
						max={maxRange}
						step={0.5}
						isRange={true}
						ariaLabel={["Minimum Bedrooms", "Maximum Bedrooms"]}
					/>
				</div>

				{/* Tick Marks */}
				<div className="relative px-3">
					<div className="flex justify-between items-center" style={{ paddingLeft: "0.5rem" }}>
						{ticks.map(tick => {
							const isSelected = tick === sliderValue[0] || tick === sliderValue[1];

							return (
								<div key={tick} className="flex flex-col items-center gap-1">
									<div className={`w-px h-2 ${isSelected ? "bg-primary-600" : "bg-gray-400"}`} />
									<span
										className={`text-xs font-semibold ${isSelected ? "text-primary-600" : "text-gray-600"}`}
										style={{ padding: "0 2px" }}
									>
										{tick === 6 ? "6+" : tick.toString()}
									</span>
								</div>
							);
						})}
					</div>
				</div>
			</div>

			{/* Input Fields */}
			<div className="space-y-3">
				<div className="grid grid-cols-2 gap-4">
					<div>
						<label className="block text-xs text-gray-500 mb-1">Min Bedrooms</label>
						<div className="relative">
							<input
								type="number"
								aria-label="Minimum Bedrooms"
								min={0}
								max={maxRange}
								step={0.5}
								value={value.min}
								onChange={e => {
									const inputValue = Number(e.target.value);
									// Cap the value between minRange and maxRange
									const cappedValue = Math.max(minRange, Math.min(inputValue, maxRange));
									onChange({ ...value, min: Math.min(cappedValue, value.max || maxRange) });
								}}
								onKeyDown={e => {
									// Allow: backspace, delete, tab, escape, enter
									if (
										[8, 9, 27, 13, 46].indexOf(e.keyCode) !== -1 ||
										// Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
										(e.keyCode === 65 && e.ctrlKey === true) ||
										(e.keyCode === 67 && e.ctrlKey === true) ||
										(e.keyCode === 86 && e.ctrlKey === true) ||
										(e.keyCode === 88 && e.ctrlKey === true) ||
										// Allow: home, end, left, right
										(e.keyCode >= 35 && e.keyCode <= 39)
									) {
										return;
									}
									// Allow: numbers, decimal point
									if (
										(e.shiftKey || e.keyCode < 48 || e.keyCode > 57) &&
										(e.keyCode < 96 || e.keyCode > 105) &&
										e.keyCode !== 190 &&
										e.keyCode !== 110
									) {
										e.preventDefault();
									}
								}}
								onBlur={e => {
									const inputValue = Number(e.target.value);
									const cappedValue = Math.max(minRange, Math.min(inputValue, maxRange));
									if (inputValue !== cappedValue) {
										onChange({ ...value, min: Math.min(cappedValue, value.max || maxRange) });
									}
								}}
								inputMode="numeric"
								pattern="[0-9]*(\.[0-9]+)?"
								className="text-center font-semibold text-gray-700 w-full"
							/>
						</div>
					</div>
					<div>
						<label className="block text-xs text-gray-500 mb-1">Max Bedrooms</label>
						<div className="relative">
							<input
								type="number"
								aria-label="Maximum Bedrooms"
								min={0}
								max={maxRange}
								step={0.5}
								value={value.max}
								onChange={e => {
									const inputValue = Number(e.target.value);
									// Cap the value between minRange and maxRange
									const cappedValue = Math.max(minRange, Math.min(inputValue, maxRange));
									onChange({ ...value, max: Math.max(cappedValue, value.min || minRange) });
								}}
								onKeyDown={e => {
									// Allow: backspace, delete, tab, escape, enter
									if (
										[8, 9, 27, 13, 46].indexOf(e.keyCode) !== -1 ||
										// Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
										(e.keyCode === 65 && e.ctrlKey === true) ||
										(e.keyCode === 67 && e.ctrlKey === true) ||
										(e.keyCode === 86 && e.ctrlKey === true) ||
										(e.keyCode === 88 && e.ctrlKey === true) ||
										// Allow: home, end, left, right
										(e.keyCode >= 35 && e.keyCode <= 39)
									) {
										return;
									}
									// Allow: numbers, decimal point
									if (
										(e.shiftKey || e.keyCode < 48 || e.keyCode > 57) &&
										(e.keyCode < 96 || e.keyCode > 105) &&
										e.keyCode !== 190 &&
										e.keyCode !== 110
									) {
										e.preventDefault();
									}
								}}
								onBlur={e => {
									const inputValue = Number(e.target.value);
									const cappedValue = Math.max(minRange, Math.min(inputValue, maxRange));
									if (inputValue !== cappedValue) {
										onChange({ ...value, max: Math.max(cappedValue, value.min || minRange) });
									}
								}}
								inputMode="numeric"
								pattern="[0-9]*(\.[0-9]+)?"
								className="text-center font-semibold text-gray-700 w-full"
							/>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
